import {
	DndContext,
	DragEndEvent,
	DraggableAttributes,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cva } from "class-variance-authority";
import * as React from "react";

import { faGripLines } from "@performyard/fontawesome/pro-regular-svg-icons";

import { cn } from "../../../lib/utils";

import { Icon } from "../../atoms/Icon";
import { Text } from "../../atoms/Text";
import { Chip } from "../Chip";

const orderedItemRowVariants = cva("flex items-center py-0.5 transition-all", {
	variants: {
		dragging: {
			true: "z-50 opacity-50",
		},
		disabled: {
			true: "cursor-not-allowed opacity-disabled",
			false: "",
		},
	},
	defaultVariants: {
		dragging: false,
		disabled: false,
	},
});

type DragHandlers = {
	listeners: ReturnType<typeof useSortable>["listeners"];
	attributes: DraggableAttributes;
};

type SortableItemProps<T> = {
	id: string;
	value: T;
	index: number;
	isDisabled: boolean;
	isReadOnly: boolean;
	onRemove: () => void;
	renderItem?: (
		value: T,
		index: number,
		onRemove: () => void,
		dragHandlers: DragHandlers,
		isDragging: boolean
	) => React.ReactNode;
	getLabel?: (value: T) => string;
};

const SortableItem = <T,>({
	id,
	value,
	index,
	isDisabled,
	isReadOnly,
	onRemove,
	renderItem,
	getLabel,
}: SortableItemProps<T>) => {
	const sortable = useSortable({
		id,
		disabled: isDisabled || isReadOnly,
	});

	const { setNodeRef, transform, transition, isDragging, attributes, listeners } = sortable;

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	const dragHandlers: DragHandlers = {
		listeners,
		attributes,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={cn(isDragging && "z-50")}
			role="listitem"
			aria-label={getLabel ? getLabel(value) : `Item ${index + 1}`}
		>
			{renderItem ? (
				renderItem(value, index + 1, onRemove, dragHandlers, isDragging)
			) : (
				<div
					className={cn(
						orderedItemRowVariants({
							dragging: isDragging,
							disabled: isDisabled,
						})
					)}
					data-testid={`sortable-item-${index}`}
				>
					{!isDisabled && !isReadOnly && (
						<div
							{...attributes}
							{...listeners}
							className="flex shrink-0 items-center"
							role="button"
							aria-label={`Drag to reorder item ${index + 1}`}
							tabIndex={0}
						>
							<Icon
								icon={faGripLines}
								size={24}
								color="neutral-7"
								dangerouslySetClassName="cursor-move"
								aria-hidden="true"
							/>
						</div>
					)}
					<Text size="caption" weight="regular" color="neutral-9" dangerouslySetClassName="shrink-0 px-1">
						{index + 1}.
					</Text>
					<div className="min-w-0 flex-1">
						<Chip
							label={getLabel ? getLabel(value) : String(value)}
							color="primary"
							size={24}
							onDismiss={isDisabled || isReadOnly ? undefined : onRemove}
							dangerouslySetClassName="max-w-full"
						/>
					</div>
				</div>
			)}
		</div>
	);
};

type SortableOrderedListProps<T> = {
	/**
	 * Array of values to display in the sortable list. Order determines display order.
	 */
	values: T[];
	/**
	 * Optional callback function called when items are reordered or removed.
	 * @param newValues - The new array of values in their current order
	 */
	onChange?: (newValues: T[]) => void;
	/**
	 * Whether the list is disabled (hides drag handle, disables drag and drop, and applies disabled opacity).
	 * @default false
	 */
	disabled?: boolean;
	/**
	 * Whether the list is read-only (hides drag handle and disables drag and drop, but does not change opacity).
	 * @default false
	 */
	readOnly?: boolean;
	/**
	 * Custom render function for displaying items in the list.
	 * @param value - The value to render
	 * @param index - The 1-based index of this item (display number)
	 * @param onRemove - Callback function to remove this item
	 * @param dragHandlers - Drag and drop handlers with listeners and attributes for the drag handle
	 * @param isDragging - Whether this item is currently being dragged
	 * @returns React node to render for the item
	 */
	renderItem?: (
		value: T,
		index: number,
		onRemove: () => void,
		dragHandlers: DragHandlers,
		isDragging: boolean
	) => React.ReactNode;
	/**
	 * Function to get a label string from a value. Used for default rendering when renderItem is not provided.
	 * @param value - The value to get a label for
	 * @returns String label to display
	 */
	getLabel?: (value: T) => string;
	/**
	 * Function to get a unique ID from a value. Used for drag and drop identification.
	 * @param value - The value to get an ID for
	 * @returns String ID
	 */
	getItemId?: (value: T) => string;
	/**
	 * Optional CSS classes to apply to the component container (use with caution).
	 */
	dangerouslySetClassName?: string;
	/**
	 * Test identifier for the list container (used for testing).
	 */
	"data-testid"?: string;
};

const SortableOrderedList = <T,>({
	values,
	onChange,
	disabled = false,
	readOnly = false,
	renderItem,
	getLabel,
	getItemId,
	dangerouslySetClassName,
	"data-testid": dataTestId,
}: SortableOrderedListProps<T>) => {
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		})
	);

	const defaultGetItemId = (val: T): string => {
		if (getItemId) {
			return getItemId(val);
		}
		if (typeof val === "object" && val !== null && "id" in val) {
			return String((val as { id: unknown }).id);
		}
		return typeof val === "string" ? val : JSON.stringify(val);
	};

	const handleRemove = (index: number) => {
		return () => {
			if (!disabled && !readOnly && onChange) {
				const newValues = values.filter((_, i) => i !== index);
				onChange(newValues);
			}
		};
	};

	const handleDragEnd = (event: DragEndEvent) => {
		if (disabled || readOnly || !onChange) {
			return;
		}

		const { active, over } = event;

		if (over && active.id !== over.id) {
			const oldIndex = values.findIndex((val) => defaultGetItemId(val) === active.id);
			const newIndex = values.findIndex((val) => defaultGetItemId(val) === over.id);

			if (oldIndex !== -1 && newIndex !== -1) {
				const newValues = [...values];
				const [removed] = newValues.splice(oldIndex, 1);
				newValues.splice(newIndex, 0, removed);
				onChange(newValues);
			}
		}
	};

	if (values.length === 0) {
		return null;
	}

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
			modifiers={[restrictToVerticalAxis, restrictToParentElement]}
		>
			<SortableContext items={values.map(defaultGetItemId)} strategy={verticalListSortingStrategy}>
				<div
					className={cn("flex w-full flex-col", dangerouslySetClassName)}
					data-testid={dataTestId || "sortable-ordered-list"}
					role="list"
					aria-label="Sortable list"
				>
					{values.map((value, index) => {
						const itemId = defaultGetItemId(value);

						return (
							<SortableItem
								key={itemId}
								id={itemId}
								value={value}
								index={index}
								isDisabled={disabled}
								isReadOnly={readOnly}
								onRemove={handleRemove(index)}
								renderItem={renderItem}
								getLabel={getLabel}
							/>
						);
					})}
				</div>
			</SortableContext>
		</DndContext>
	);
};

SortableOrderedList.displayName = "SortableOrderedList";

export { SortableOrderedList, type SortableOrderedListProps, type DragHandlers };
